/**
 * URL serialization for ephemeral sheets.
 *
 * Two formats:
 *
 * - **JSON blob** (`?e=<url-encoded-json>`) — the full `RawSheet`
 *   shape survives intact; good for rich fixtures with attributes
 *   or seed cells.
 * - **Flat indexed params** (`?r0.type=number&r0.label=Weight...`)
 *   — human-readable; good for hand-composed demo URLs. Limited to
 *   a subset of fields: `id`, `title` on the sheet and
 *   `id`/`type`/`label`/`ts`/`key`/`parent` on each row. Rows get
 *   auto-generated UUIDs when `rN.id` is absent.
 *
 * An optional anchor date (`?d=YYYY-MM-DD`) rides alongside either
 * format — it's not part of `RawSheet` but threads through for the
 * viewport's initial scroll target.
 *
 * Ported from `tento/tento-lona-js/src/ephemeral-sheet-serde.ts:EphemeralSheetSerde`.
 * lona-js's shape carries a sheet-level `timeScale` field that
 * `RawSheet` doesn't yet model — the sheet-level key is ignored
 * here. Per-row `timeScale` survives in both formats.
 */

import type { Option } from "../core/option.ts";
import { Json } from "../core/json.ts";
import type { RawRow, RawSheet } from "./raw-sheet.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import { SheetNetworkKey } from "../ids/sheet-network-key.ts";

/** Parse result — the decoded sheet plus an optional anchor date. */
export interface ParsedEphemeral {
  readonly sheet: RawSheet;
  readonly anchorDate?: string;
}

/**
 * Parse a `?e=` JSON payload at the URL boundary. A shallow shape check —
 * an object with a string `id` and a `rows` array whose entries carry string
 * `id` + `type` (the `RawSheet` / `RawRow` required fields) — so a hand-edited
 * or truncated link fails here, named, instead of deep inside hydrate.
 */
function decodeRawSheetJson(text: string, label: string): RawSheet {
  const parsed: Option<Json> = Json.wire(JSON.parse(text));
  if (!isRawSheetShape(parsed)) {
    throw new TypeError(
      `${label}: payload is not a RawSheet ` +
        `(expected { id: string, rows: { id: string, type: string }[] })`,
    );
  }
  return parsed;
}

function isJsonRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isRawSheetShape(value: unknown): value is RawSheet {
  return isJsonRecord(value) &&
    typeof value.id === "string" &&
    Array.isArray(value.rows) &&
    value.rows.every((row: unknown) =>
      isJsonRecord(row) && typeof row.id === "string" &&
      typeof row.type === "string"
    );
}

/** Encodes an ephemeral sheet into a URL parameter and decodes it back. */
export const EphemeralSheetSerde = {
  // ── Format 1: JSON blob ────────────────────────────────────────

  /** Encode full sheet as URL-safe JSON — the value of a `?e=` param. */
  encodeJson(sheet: RawSheet): string {
    return encodeURIComponent(JSON.stringify(sheet));
  },

  /**
   * Decode from a `?e=` param value. Throws on malformed JSON, and a
   * `TypeError` naming the boundary when the payload is not a `RawSheet`.
   */
  decodeJson(encoded: string): RawSheet {
    return decodeRawSheetJson(
      decodeURIComponent(encoded),
      "EphemeralSheetSerde.decodeJson",
    );
  },

  /** Build a `?e=`-form URL path; optionally include `&d=<anchorDate>`. */
  buildJsonUrl(sheet: RawSheet, anchorDate: Option<string>): string {
    const params = new URLSearchParams();
    params.set("e", JSON.stringify(sheet));
    if (anchorDate) params.set("d", anchorDate);
    return `/sheet?${params.toString()}`;
  },

  // ── Format 2: flat indexed params ──────────────────────────────

  /** Encode as flat `rN.<field>` params. `title` / `id` land unprefixed. */
  encodeIndexed(sheet: RawSheet): URLSearchParams {
    const params = new URLSearchParams();
    if (sheet.id) params.set("id", sheet.id);
    if (sheet.title) params.set("title", sheet.title);
    for (const [i, r] of sheet.rows.entries()) {
      params.set(`r${i}.type`, r.type);
      if (r.id) params.set(`r${i}.id`, r.id);
      if (r.label) params.set(`r${i}.label`, r.label);
      if (r.timeScale) params.set(`r${i}.ts`, r.timeScale);
      if (r.lookupKey) params.set(`r${i}.key`, r.lookupKey);
      if (r.parentId) params.set(`r${i}.parent`, r.parentId);
    }
    return params;
  },

  /** Decode `rN.<field>` params back into a `RawSheet`. */
  decodeIndexed(params: URLSearchParams): RawSheet {
    const rows: RawRow[] = [];
    for (let i = 0; ; i++) {
      const type = params.get(`r${i}.type`);
      if (!type) break;
      const label = params.get(`r${i}.label`);
      const timeScale = params.get(`r${i}.ts`);
      const lookupKey = params.get(`r${i}.key`);
      const parentId = params.get(`r${i}.parent`);
      const row: RawRow = {
        id: RowNetworkKey.fromWire(params.get(`r${i}.id`) ?? crypto.randomUUID()),
        type,
        ...(label !== null ? { label } : {}),
        ...(timeScale !== null ? { timeScale } : {}),
        ...(lookupKey !== null ? { lookupKey } : {}),
        ...(parentId !== null ? { parentId: RowNetworkKey.fromWire(parentId) } : {}),
      };
      rows.push(row);
    }
    const title = params.get("title");
    return {
      id: SheetNetworkKey.fromWire(params.get("id") ?? crypto.randomUUID()),
      ...(title !== null ? { title } : {}),
      rows,
    };
  },

  /** Build a flat-indexed URL path; optionally include `&d=<anchorDate>`. */
  buildIndexedUrl(sheet: RawSheet, anchorDate: Option<string>): string {
    const params = EphemeralSheetSerde.encodeIndexed(sheet);
    if (anchorDate) params.set("d", anchorDate);
    return `/sheet?${params.toString()}`;
  },

  // ── Parser — auto-detects format ───────────────────────────────

  /**
   * Walk a URL's query params and return the parsed sheet + anchor
   * date, or `null` if neither format is recognised. Tries the
   * `?e=` JSON form first, then the flat-indexed form keyed off
   * `r0.type`.
   */
  parse(params: URLSearchParams): Option<ParsedEphemeral> {
    const encoded = params.get("e");
    if (encoded !== null) {
      try {
        const sheet = decodeRawSheetJson(encoded, "EphemeralSheetSerde.parse");
        const anchorDate = params.get("d") ?? undefined;
        return anchorDate !== undefined ? { sheet, anchorDate } : { sheet };
      } catch {
        return null;
      }
    }
    if (params.has("r0.type")) {
      try {
        const sheet = EphemeralSheetSerde.decodeIndexed(params);
        const anchorDate = params.get("d") ?? undefined;
        return anchorDate !== undefined ? { sheet, anchorDate } : { sheet };
      } catch {
        return null;
      }
    }
    return null;
  },
} as const;
